import React from "react";
import {
	Box,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Grid2 as Grid,
	Typography,
	useTheme,
	useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useStyles } from "./CreditRequest.styles";
import { CreditRequestHeaderProps } from "./types";
import StatusLabel from "../StatusLabel/StatusLabel";
import { getTimestampInLocalTime } from "@/util/dateUtil";
import { creditStatus } from "../../util/creditRequestMapping";

export default function CreditRequestHeader(props: CreditRequestHeaderProps) {
	const {
		expanded,
		onExpandClick,
		friendlyId,
		status,
		purchaseOrderNumber,
		invoiceNumber,
		locationId,
		locationName,
		supplierName,
		type,
		totalCostInc,
		dateModified,
		dateCreated
	} = props;
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	return (
		<Accordion defaultExpanded expanded={expanded} square={true}>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
				onClick={() => onExpandClick()}
				aria-controls="creditrequestheader-content"
				id="creditrequestheader"
				className={classes.collapsePanelSummary}
			>
				<Grid container alignItems="center" sx={{ width: '100%' }}>
					<Grid size="auto">
						<Typography variant="caption" color="textSecondary" display="block">
							Credit Request No
						</Typography>
						<Typography className={classes.value} variant="body2" display="block" color="textPrimary">
							{friendlyId}
						</Typography>
					</Grid>
					<Grid container justifyContent="flex-end" size="grow">
						<StatusLabel
							status={status}
							size={isMobile ? "small" : "medium"}
							sx={
								status === creditStatus.reviewing
									? (theme) => ({
											"&&": {
												backgroundColor: theme.palette.custom.creditRequest.reviewingBadge,
												border:
													theme.palette.mode === "dark"
														? `1px solid ${theme.palette.text.primary}`
														: "none",
											},
										})
									: undefined
							}
						/>
					</Grid>
				</Grid>
			</AccordionSummary>
			<AccordionDetails>
				<Grid container>
					<Grid size={6} sx={{ display: 'flex', flexDirection: 'column' }}>
						<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
							Purchase Order
						</Typography>
						<Typography className={classes.value} variant="body2" display="inline">
							{purchaseOrderNumber}
						</Typography>
						{invoiceNumber && (
							<>
								<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
									Invoice Number
								</Typography>
							
								<Typography className={classes.value} variant="body2" display="inline">
									{invoiceNumber}
								</Typography>
							</>
						)}
						<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
							Type
						</Typography>
						<Typography className={classes.value} variant="body2" display="inline">
							{type ?? "Standard"}
						</Typography>
						<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
							Date Created
						</Typography>
						<Typography className={classes.value} variant="body2" display="inline">
							{getTimestampInLocalTime(dateCreated)}
						</Typography>
					</Grid>
					<Grid size={6} sx={{ display: 'flex', flexDirection: 'column' }}>
						<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
							Store
						</Typography>
						<Typography className={classes.value} variant="body2" display="inline">
							{locationId + " - " + locationName}
						</Typography>
						<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
							Supplier
						</Typography>
						<Typography className={classes.value} variant="body2" display="inline">
							{supplierName}
						</Typography>
						<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
							Total Cost Inc
						</Typography>
						<Typography className={classes.value} variant="body2" display="inline">
							{"$" + (totalCostInc ?? 0).toFixed(2)}
						</Typography>
						<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
							Last Modified Date
						</Typography>
						<Typography className={classes.value} variant="body2" display="inline">
							{getTimestampInLocalTime(dateModified)}
						</Typography>
					</Grid>
				</Grid>
			</AccordionDetails>
		</Accordion>
	);
}
